import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Loading,
  LoadingIcon,
  Modal,
} from 'vet-bones/design-system/components';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  CONFIGURE_ORG_SSO_DIALOG_CANCEL,
  CONFIGURE_ORG_SSO_DIALOG_DELETE,
  CONFIGURE_ORG_SSO_DIALOG_EDIT,
  CONFIGURE_ORG_SSO_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import {
  ConfigureOrgSSOEntraForm,
  EntraFormData,
  buildEntraCreateInput,
  buildEntraUpdateInput,
  getInitialEntraFormData,
  isEntraCreateValid,
  isEntraEditDirty,
  isEntraEditValid,
  populateEntraForm,
} from 'src/ui/containers/organization/dialogs/ConfigureOrgSSOEntraForm';
import {
  ConfigureOrgSSOOktaForm,
  OktaFormData,
  buildOktaCreateInput,
  buildOktaUpdateInput,
  getInitialOktaFormData,
  isOktaCreateValid,
  isOktaEditDirty,
  isOktaEditValid,
  populateOktaForm,
} from 'src/ui/containers/organization/dialogs/ConfigureOrgSSOOktaForm';
import { ConfigureOrgSSOSelectProvider } from 'src/ui/containers/organization/dialogs/ConfigureOrgSSOSelectProvider';
import { ConfigureOrgSSOView } from 'src/ui/containers/organization/dialogs/ConfigureOrgSSOView';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  OrganizationMicrosoftEntraSsoConfig,
  OrganizationOktaSsoConfig,
  SsoProviderType,
  useCreateOrganizationMicrosoftEntraSsoConfigMutation,
  useCreateOrganizationOktaSsoConfigMutation,
  useDeleteOrganizationSsoConfigMutation,
  useUpdateOrganizationMicrosoftEntraSsoConfigMutation,
  useUpdateOrganizationOktaSsoConfigMutation,
} from 'src/ui/graph/types';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import {
  useInvalidateOrganization,
  useOrganization,
} from 'src/ui/hooks/organization';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const isOpenSelector = isDialogOpenSelector(
  DialogType.CONFIGURE_ORGANIZATION_SSO
);

type DialogMode = 'select-provider' | 'create' | 'view' | 'edit';

export const ConfigureOrgSSODialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const onError = useOnError();
  const onMutate = useOnMutate();
  const { org = '' } = useParams<{ org?: string }>();

  const isOpen = useAppSelector(isOpenSelector);

  const { organization, isLoadingOrganization } = useOrganization(org);
  const invalidateOrg = useInvalidateOrganization(org);
  const existingConfig = organization?.ssoConfig ?? null;

  const [mode, setMode] = React.useState<DialogMode>('select-provider');
  const [
    selectedProvider,
    setSelectedProvider,
  ] = React.useState<SsoProviderType | null>(null);
  const [oktaForm, setOktaForm] = React.useState<OktaFormData>(
    getInitialOktaFormData()
  );
  const [entraForm, setEntraForm] = React.useState<EntraFormData>(
    getInitialEntraFormData()
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  // Mutations
  const {
    mutate: createOkta,
    isLoading: isCreatingOkta,
  } = useCreateOrganizationOktaSsoConfigMutation(graphQLClient, {
    onError,
    onMutate,
    onSuccess: () => {
      invalidateOrg();
      setMode('view');
    },
  });
  const {
    mutate: createEntra,
    isLoading: isCreatingEntra,
  } = useCreateOrganizationMicrosoftEntraSsoConfigMutation(graphQLClient, {
    onError,
    onMutate,
    onSuccess: () => {
      invalidateOrg();
      setMode('view');
    },
  });
  const {
    mutate: updateOkta,
    isLoading: isUpdatingOkta,
  } = useUpdateOrganizationOktaSsoConfigMutation(graphQLClient, {
    onError,
    onMutate,
    onSuccess: () => {
      invalidateOrg();
      setMode('view');
    },
  });
  const {
    mutate: updateEntra,
    isLoading: isUpdatingEntra,
  } = useUpdateOrganizationMicrosoftEntraSsoConfigMutation(graphQLClient, {
    onError,
    onMutate,
    onSuccess: () => {
      invalidateOrg();
      setMode('view');
    },
  });
  const {
    mutate: deleteSSOConfig,
    isLoading: isDeleting,
  } = useDeleteOrganizationSsoConfigMutation(graphQLClient, {
    onError,
    onMutate,
    onSuccess: () => {
      invalidateOrg();
      handleClose();
    },
  });

  const isOkta = selectedProvider === SsoProviderType.Okta;
  const isEntra = selectedProvider === SsoProviderType.MicrosoftEntra;

  // Determine initial mode based on existing config
  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (existingConfig) {
      setMode('view');
      setSelectedProvider(existingConfig.providerType);
    } else {
      setMode('select-provider');
      setSelectedProvider(null);
    }
  }, [isOpen, existingConfig]);

  const handleClose = () => {
    setMode('select-provider');
    setSelectedProvider(null);
    setOktaForm(getInitialOktaFormData());
    setEntraForm(getInitialEntraFormData());
    setShowDeleteConfirm(false);
    dispatch(closedDialog());
  };

  const handleStartEdit = () => {
    if (!existingConfig) {
      return;
    }
    if (isOkta) {
      setOktaForm(
        populateOktaForm(existingConfig as OrganizationOktaSsoConfig)
      );
    } else if (isEntra) {
      setEntraForm(
        populateEntraForm(existingConfig as OrganizationMicrosoftEntraSsoConfig)
      );
    }
    setMode('edit');
  };

  let title = '';
  let body: JSX.Element | null = null;
  let footerActions: JSX.Element | null = null;
  if (isLoadingOrganization) {
    title = copy.components.configureOrgSSO.selectProvider.title;
    body = (
      <div className="organization-dialog-loading">
        <Loading size="lg" />
      </div>
    );
    footerActions = (
      <>
        <Button
          onClick={handleClose}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_CANCEL}
          variant="ghost-gray"
        >
          {copy.components.configureOrgSSO.selectProvider.buttons.cancel}
        </Button>
      </>
    );
  } else if (mode === 'select-provider') {
    title = copy.components.configureOrgSSO.selectProvider.title;
    body = (
      <ConfigureOrgSSOSelectProvider
        onSelectProvider={setSelectedProvider}
        selectedProvider={selectedProvider}
      />
    );
    footerActions = (
      <>
        <Button
          onClick={handleClose}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_CANCEL}
          variant="ghost-gray"
        >
          {copy.components.configureOrgSSO.selectProvider.buttons.cancel}
        </Button>
        <Button
          disabled={!selectedProvider}
          onClick={() => setMode('create')}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_SUBMIT}
          variant="primary-accent"
        >
          {copy.components.configureOrgSSO.selectProvider.buttons.submit}
        </Button>
      </>
    );
  } else if (mode === 'create' && selectedProvider === SsoProviderType.Okta) {
    const isValid = isOktaCreateValid(oktaForm);
    title = copy.components.configureOrgSSO.create.title(
      copy.components.configureOrgSSO.dialog.providers.okta
    );
    body = (
      <ConfigureOrgSSOOktaForm
        form={oktaForm}
        isCreate
        onChange={setOktaForm}
      />
    );
    footerActions = (
      <>
        <Button
          onClick={() => {
            setOktaForm(getInitialOktaFormData());
            setMode('select-provider');
          }}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_CANCEL}
          variant="ghost-gray"
        >
          {copy.components.configureOrgSSO.create.buttons.cancel}
        </Button>
        <Button
          disabled={!isValid}
          onClick={() =>
            createOkta({ domain: org, input: buildOktaCreateInput(oktaForm) })
          }
          rightIcon={isCreatingOkta ? LoadingIcon : undefined}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_SUBMIT}
          variant="primary-accent"
        >
          {copy.components.configureOrgSSO.create.buttons.submit}
        </Button>
      </>
    );
  } else if (
    mode === 'create' &&
    selectedProvider === SsoProviderType.MicrosoftEntra
  ) {
    const isValid = isEntraCreateValid(entraForm);
    title = copy.components.configureOrgSSO.create.title(
      copy.components.configureOrgSSO.dialog.providers.microsoftEntra
    );
    body = (
      <ConfigureOrgSSOEntraForm
        form={entraForm}
        isCreate
        onChange={setEntraForm}
      />
    );
    footerActions = (
      <>
        <Button
          onClick={() => {
            setEntraForm(getInitialEntraFormData());
            setMode('select-provider');
          }}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_CANCEL}
          variant="ghost-gray"
        >
          {copy.components.configureOrgSSO.create.buttons.cancel}
        </Button>
        <Button
          disabled={!isValid}
          onClick={() =>
            createEntra({
              domain: org,
              input: buildEntraCreateInput(entraForm),
            })
          }
          rightIcon={isCreatingEntra ? LoadingIcon : undefined}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_SUBMIT}
          variant="primary-accent"
        >
          {copy.components.configureOrgSSO.create.buttons.submit}
        </Button>
      </>
    );
  } else if (mode === 'view') {
    title = copy.components.configureOrgSSO.view.title;
    body = existingConfig ? (
      <ConfigureOrgSSOView
        config={existingConfig}
        isDeleting={isDeleting}
        onDelete={() => deleteSSOConfig({ domain: org })}
        onShowDeleteConfirm={setShowDeleteConfirm}
        orgDomain={org}
        showDeleteConfirm={showDeleteConfirm}
      />
    ) : (
      <div className="organization-dialog-loading">
        <Loading size="lg" />
      </div>
    );
    footerActions = (
      <>
        {!showDeleteConfirm ? (
          <Button
            onClick={() => setShowDeleteConfirm(true)}
            size="sm"
            testId={CONFIGURE_ORG_SSO_DIALOG_DELETE}
            variant="ghost-gray"
          >
            {copy.components.configureOrgSSO.view.buttons.delete}
          </Button>
        ) : null}
        <Button
          onClick={handleStartEdit}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_EDIT}
          variant="ghost-gray"
        >
          {copy.components.configureOrgSSO.view.buttons.edit}
        </Button>
        <Button
          onClick={handleClose}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_SUBMIT}
          variant="primary-accent"
        >
          {copy.components.configureOrgSSO.view.buttons.submit}
        </Button>
      </>
    );
  } else if (mode === 'edit' && isOkta) {
    const isValid =
      isOktaEditValid(oktaForm) &&
      (!existingConfig ||
        isOktaEditDirty(oktaForm, existingConfig as OrganizationOktaSsoConfig));
    title = copy.components.configureOrgSSO.edit.title(
      copy.components.configureOrgSSO.dialog.providers.okta
    );
    body = (
      <ConfigureOrgSSOOktaForm
        form={oktaForm}
        isCreate={false}
        onChange={setOktaForm}
      />
    );
    footerActions = (
      <>
        <Button
          disabled={isUpdatingOkta}
          onClick={() => setMode('view')}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_CANCEL}
          variant="ghost-gray"
        >
          {copy.components.configureOrgSSO.edit.buttons.cancel}
        </Button>
        <Button
          disabled={!isValid || isUpdatingOkta}
          onClick={() =>
            updateOkta({
              domain: org,
              input: buildOktaUpdateInput(oktaForm),
            })
          }
          rightIcon={isUpdatingOkta ? LoadingIcon : undefined}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_SUBMIT}
          variant="primary-accent"
        >
          {copy.components.configureOrgSSO.edit.buttons.submit}
        </Button>
      </>
    );
  } else if (mode === 'edit' && isEntra) {
    const isValid =
      isEntraEditValid(entraForm) &&
      (!existingConfig ||
        isEntraEditDirty(
          entraForm,
          existingConfig as OrganizationMicrosoftEntraSsoConfig
        ));
    title = copy.components.configureOrgSSO.edit.title(
      copy.components.configureOrgSSO.dialog.providers.microsoftEntra
    );
    body = (
      <ConfigureOrgSSOEntraForm
        form={entraForm}
        isCreate={false}
        onChange={setEntraForm}
      />
    );
    footerActions = (
      <>
        <Button
          disabled={isUpdatingEntra}
          onClick={() => setMode('view')}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_CANCEL}
          variant="ghost-gray"
        >
          {copy.components.configureOrgSSO.edit.buttons.cancel}
        </Button>
        <Button
          disabled={!isValid || isUpdatingEntra}
          onClick={() =>
            updateEntra({
              domain: org,
              input: buildEntraUpdateInput(entraForm),
            })
          }
          rightIcon={isUpdatingEntra ? LoadingIcon : undefined}
          size="sm"
          testId={CONFIGURE_ORG_SSO_DIALOG_SUBMIT}
          variant="primary-accent"
        >
          {copy.components.configureOrgSSO.edit.buttons.submit}
        </Button>
      </>
    );
  }

  return (
    <Modal
      canOutsideClickClose={false}
      className="organization-dialog"
      footerActions={footerActions}
      headerSize="lg"
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
    >
      <div className="organization-dialog-body">{body}</div>
    </Modal>
  );
};
